import { inject, Injectable } from '@angular/core';
import { AppSettings } from '../interfaces/app-settings.interface';
import { DEFAULT_SETTINGS } from '../pages/settings-page/default.settings.model';
import { SettingOption } from '../interfaces/settings-option.interface';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private settings: AppSettings = JSON.parse(JSON.stringify(DEFAULT_SETTINGS));
  private translocoService = inject(TranslocoService);

  constructor() {
    this.loadSettings();
  }

  public init(): Promise<void> {
    return new Promise((resolve) => {
      this.loadSettings();
      resolve();
    });
  }

  public get currentSettings(): AppSettings {
    return this.settings;
  }

  public updateSetting<T>(newValue: T, option: SettingOption<T>): void {
    switch (option.key) {
      case '5s_lang':
        this.updateLanguage(newValue as string);
        break;
      case '5s_font_size':
        this.updateFontSize(newValue as string);
        break;
    }
  }

  public isFontSetting<T>(option: SettingOption<T>): boolean {
    return option.key === '5s_font_size';
  }

  public isLanguageSetting<T>(option: SettingOption<T>): boolean {
    return option.key === '5s_lang';
  }

  private loadSettings(): void {

    const settingKeys = Object.keys(this.settings) as (keyof AppSettings)[];

    settingKeys.forEach((key) => {
      const setting = this.settings[key];

      const savedValue = localStorage.getItem(setting.key);
      const vale = setting.availableValues.find((value) => value.value === savedValue);
      setting.selectedValue = vale || setting.defaultValue;

      switch (key) {
        case 'language':
          this.applyLanguageTag(setting.selectedValue.value);
          break;
        case 'fontSize':
          this.applyFontSize(setting.selectedValue.value);
          break;
      }
    });
  }

  private updateLanguage(newLang: string): void {
    const language = this.settings.language;

    language.selectedValue = language.availableValues.find(v => v.value === newLang) ?? language.defaultValue;

    localStorage.setItem(language.key, language.selectedValue.value);
    this.applyLanguageTag(language.selectedValue.value);
  }

  private updateFontSize(newSize: string): void {
    const fontSize = this.settings.fontSize;

    fontSize.selectedValue = fontSize.availableValues.find(v => v.value === newSize) ?? fontSize.defaultValue;

    localStorage.setItem(fontSize.key, fontSize.selectedValue.value);
    this.applyFontSize(fontSize.selectedValue.value);
  }

  private applyFontSize(size: string): void {
    const root = document.documentElement;
    root.classList.remove('font-small', 'font-medium', 'font-large');
    root.classList.add(`font-${size}`);
  }

  private applyLanguageTag(lang: string): void {
    document.documentElement.lang = lang;
    this.translocoService.setActiveLang(lang);
  }
}
